import React, { useEffect, useState } from "react";
import {
  UpperContainer,
  FilterChipContainer,
  FilterContainer,
  MeetingRoomFilterSection,
  MainContainer,
  MeetingRoomListContainer,
  EmptyItem,
} from "./MinimumAttendeesSetting.styles";
import { MeetingRoomLocationFilter, MeetingRoomFilter } from "uis/Filters";
import UI from "@mile-meeting/mile-ui";
import { MeetingRoomCard } from "uis/MinimumAttendeesManagement";
import {
  getBuildingListForLocation,
  getMeetingRoomList,
  TLocationFilter,
  TMeetingRoom,
} from "apis/meetings/meeting-room";
import { TBuilding } from "types/building.types";
import { LocationDataType } from "uis/Filters/MeetingRoomLocationFilter/MeetingRoomLocationFilter";
import { AmplitudeService } from "utils/amplitude.service";
import { useRecoilValue } from "recoil";
import { workspace } from "recoil/recoilState";

type MinimumAttendeesSettingProps = {
  isShow: boolean;
};

const MinimumAttendeesSetting: React.FC<MinimumAttendeesSettingProps> = ({ isShow }) => {
  const [meetingRoomList, setMeetingRoomList] = useState<TMeetingRoom[]>([]);
  const [page, setPage] = useState<number>(1);
  const [totalPage, setTotalPage] = useState<number>(1);
  const [buildingInfo, setBuildingInfo] = useState<(TBuilding | null)[]>([]);
  const [orgFilterMeetingRoomList, setOrgFilterMeetingRoomList] = useState<TMeetingRoom[]>([]);
  const [filteredMeetingRoomList, setFilteredMeetingRoomList] = useState<TMeetingRoom[]>([]);
  const [filter, setFilter] = useState<TLocationFilter[]>([]);

  const attendeeFilter = ["ALL", "SET", "NOT_SET"] as const;
  type TAttendeeFilter = "ALL" | "SET" | "NOT_SET";
  const filterChips: Record<TAttendeeFilter, string> = {
    ALL: "전체 회의실",
    SET: "설정한 회의실",
    NOT_SET: "미설정한 회의실",
  };
  const [selectedFilterChip, setSelectedFilterChip] = useState<TAttendeeFilter>("ALL");

  const handleChangePage = (page: number) => {
    let meetingRoomIds = filteredMeetingRoomList.filter((item) => item.id !== "").map((item) => item.id) ?? [];
    getMeetingRoomList(page, filter, meetingRoomIds, 6, false, selectedFilterChip).then((res) => {
      setMeetingRoomList(res.data?.meetingRoomList ?? []);
      setTotalPage(res.data?.totalPageNumber ?? 1);
      setPage(page);
    });
  };

  useEffect(() => {
    getMeetingRoomList(1, filter, [], 6, false).then((res) => {
      setMeetingRoomList(res.data?.meetingRoomList ?? []);
      setTotalPage(res.data?.totalPageNumber ?? 1);
    });

    getBuildingListForLocation().then((res) => {
      if (res.success) {
        setBuildingInfo(res.data ?? []);
      }
    });
  }, []);

  useEffect(() => {
    let meetingRoomIds = filteredMeetingRoomList.filter((item) => item.id !== "").map((item) => item.id) ?? [];
    getMeetingRoomList(1, filter, meetingRoomIds, 6, false, selectedFilterChip).then((res) => {
      setMeetingRoomList(res.data?.meetingRoomList ?? []);
      setTotalPage(res.data?.totalPageNumber ?? 1);
    });

    getMeetingRoomList(1, filter, meetingRoomIds, 100, false, selectedFilterChip).then((res) => {
      setOrgFilterMeetingRoomList(res.data?.meetingRoomList ?? []);
    });
  }, [selectedFilterChip]);

  return (
    <div
      style={{
        display: isShow ? "flex" : "none",
        flexDirection: "column",
        gap: 24,
        minHeight: "calc(100vh - 354px)",
        width: "100%",
      }}
    >
      <UpperContainer>
        <MeetingRoomFilterSection>
          <FilterContainer>
            <UI.Typography type="body01 normal" fontStyle="medium" color="var(--alias-color-label-normal)">
              회의실 위치
            </UI.Typography>
            <MeetingRoomLocationFilter
              locationData={buildingInfo}
              onChange={(value) => {
                const transformedFilter = value
                  //* INFO: 전체 건물 & 전체 층은, item.building.id가 undefined이다. => 전체 건물을 필터에서 없애는 용도
                  .filter((item) => item.building === null || item.building?.id !== undefined)
                  .map((item: LocationDataType) => {
                    if (item.building === null) return null;
                    else {
                      return {
                        id: item.building?.id ?? "",
                        name: item.building?.name ?? "",
                        floors: item.floor
                          ? item.floor.map((floorItem: { name: string; id: string } | null) => {
                              if (floorItem === null) return null;
                              else
                                return floorItem
                                  ? {
                                      id: floorItem?.id ?? "",
                                      name: floorItem?.name ?? "",
                                    }
                                  : null;
                            })
                          : [],
                      };
                    }
                  });

                setFilter(transformedFilter);
                getMeetingRoomList(1, transformedFilter, [], 6, false).then((res) => {
                  if (res.success) {
                    setFilteredMeetingRoomList([]);

                    //* INFO: 필터링된 회의실 리스트를 저장 (회의실 필터링을 위해)
                    //TODO: 추후에 필터링된 회의실 리스트를 저장하는 방법을 수정해야함
                    getMeetingRoomList(1, transformedFilter, [], 100, false).then((res) => {
                      setOrgFilterMeetingRoomList(res.data?.meetingRoomList ?? []);
                    });

                    setTotalPage(res.data?.totalPageNumber ?? 1);
                    setPage(res.data?.nowPageNumber ?? 1);
                    setFilter(transformedFilter);
                    setMeetingRoomList(res.data?.meetingRoomList ?? []);
                  }
                });
              }}
            />
          </FilterContainer>
          <FilterContainer>
            <UI.Typography type="body01 normal" fontStyle="medium" color="var(--alias-color-label-normal)">
              회의실
            </UI.Typography>
            <MeetingRoomFilter
              value={filteredMeetingRoomList}
              onChange={(value) => {
                setFilteredMeetingRoomList(value ?? []);

                let meetingRoomIds = value.filter((item) => item.id !== "").map((item) => item.id) ?? [];
                getMeetingRoomList(1, filter, meetingRoomIds, 100, false).then((res) => {
                  if (res.success) {
                    setMeetingRoomList(res.data?.meetingRoomList ?? []);
                    setTotalPage(res.data?.totalPageNumber ?? 1);
                    setPage(res.data?.nowPageNumber ?? 1);
                  }
                });
              }}
              meetingRoomList={orgFilterMeetingRoomList}
            />
          </FilterContainer>
        </MeetingRoomFilterSection>
        <FilterChipContainer>
          {attendeeFilter.map((chip, index) => (
            <UI.Chip.Outlined
              selected={selectedFilterChip === chip}
              key={index}
              title={filterChips[chip]}
              onClick={() => {
                if (selectedFilterChip === chip) return;
                setSelectedFilterChip(chip as TAttendeeFilter);
              }}
            />
          ))}
        </FilterChipContainer>
      </UpperContainer>
      <MainContainer>
        {meetingRoomList.length > 0 && (
          <MeetingRoomListContainer>
            {meetingRoomList.map((item, index) => {
              return (
                <MeetingRoomCard
                  key={index}
                  meetingRoom={item}
                  onUpdate={() => {
                    handleChangePage(page);
                  }}
                />
              );
            })}
          </MeetingRoomListContainer>
        )}
        {meetingRoomList.length === 0 && (
          <EmptyItem>
            <UI.Icon name="meetingRoom2" color="var(--alias-color-label-alternative)" size={24} />
            <UI.Typography type="body01 normal" fontStyle="regular" color="var(--alias-color-label-normal)">
              등록된 회의실이 없습니다. 회의실 등록 후, 설정을 진행해 주세요.
            </UI.Typography>
          </EmptyItem>
        )}
        <UI.Pagination pageCount={totalPage} currentPage={page} onChange={handleChangePage} />
      </MainContainer>
    </div>
  );
};

export default React.memo(MinimumAttendeesSetting);
